import { useRef, useState } from "react";
import { get, getRaw, post } from "../common/api/apiShared";
import { useDotLegalMutation, useDotLegalQueryClient } from "../common/hooks/dotLegalReactQuery";
import {
    AiChatActivityEntry,
    AiChatAttachment,
    AiChatConversationDetail,
    AiChatEntityReference,
    AiChatMessage,
    AiChatSourceReference,
    AiChatStreamEvent,
} from "./AiChat.types";
import { aiChatAttachmentsQueryKey } from "./attachments/AiChatAttachment.hooks";
import { aiChatConversationsQueryKey } from "./sidebar/AiChatSidebar.hooks";
import { useAiChatState } from "./AiChatContext";

export function useAiChat() {
    const [messages, setMessages] = useState<AiChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const conversationCreationRef = useRef<Promise<string> | null>(null);
    const queryClient = useDotLegalQueryClient();
    const { getScope, bindTransientScopeToConversation } = useAiChatState();

    const createConversationMutation = useDotLegalMutation<string, void>(async () => {
        const result = await post<{ id: string }>("/ai-chat/conversations", {});
        return result.value().id;
    });

    async function ensureConversationId(): Promise<string> {
        if (conversationId !== null) return conversationId;
        if (conversationCreationRef.current) return conversationCreationRef.current;

        const creation = createConversationMutation.mutateAsync().then((id) => {
            bindTransientScopeToConversation(id);
            setConversationId(id);
            queryClient.refetchQueries(aiChatConversationsQueryKey);
            return id;
        });

        conversationCreationRef.current = creation;
        try {
            return await creation;
        } finally {
            conversationCreationRef.current = null;
        }
    }

    function startNewChat() {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        conversationCreationRef.current = null;
        setMessages([]);
        setConversationId(null);
        setIsStreaming(false);
    }

    async function loadConversation(idToLoad: string) {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const detail = await get<AiChatConversationDetail>(`/ai-chat/conversations/${idToLoad}`);
        const hydrated: AiChatMessage[] = detail.messages.map((m) => ({
            id: m.id,
            role: m.role === 0 ? "user" : "assistant",
            content: m.content,
            sources: m.sources,
            attachments: m.attachments,
            feedbackIsPositive: m.feedbackIsPositive,
        }));
        setMessages(hydrated);
        setConversationId(detail.id);
        setIsStreaming(false);
    }

    async function sendMessage(text: string, attachments?: AiChatAttachment[]) {
        if (!text.trim() || isStreaming) return;

        const userMessage: AiChatMessage = {
            role: "user",
            content: text,
            attachments: attachments && attachments.length > 0 ? attachments : undefined,
        };
        setMessages((prev) => [...prev, userMessage]);
        setIsStreaming(true);
        const controller = new AbortController();
        abortControllerRef.current = controller;

        let streamedContent = "";
        let messageId: string | undefined;
        let sources: AiChatSourceReference[] | undefined;
        let entityReferences: AiChatEntityReference[] | undefined;
        let resultsTruncated = false;
        let usedDeepAnalysis = false;
        const activity: AiChatActivityEntry[] = [];

        function updateCurrentAssistantMessage() {
            setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    id: messageId,
                    role: "assistant",
                    content: streamedContent,
                    activity: activity.length > 0 ? [...activity] : undefined,
                    sources,
                    entityReferences,
                    resultsTruncated,
                    usedDeepAnalysis: usedDeepAnalysis ? true : undefined,
                };
                return updated;
            });
        }

        function appendActivity(kind: AiChatActivityEntry["kind"], text: string, toolName?: string, attachmentId?: string) {
            const last = activity[activity.length - 1];
            if (last && last.kind === kind && kind === "thinking") {
                activity[activity.length - 1] = { ...last, text: last.text + text };
            } else {
                activity.push({ kind, text, toolName, attachmentId });
            }
            updateCurrentAssistantMessage();
        }

        setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

        try {
            // If the picker already kicked off a conversation create (because the user
            // attached files in the same tick as sending), wait for it so we don't
            // race the BE into producing a second auto-created conversation.
            const pendingCreation = conversationCreationRef.current;
            const resolvedConversationId = conversationId ?? (pendingCreation ? await pendingCreation : null);

            const scope = getScope(resolvedConversationId);
            const body: Record<string, unknown> = { message: text };
            if (resolvedConversationId) body.conversationId = resolvedConversationId;
            if (scope) body.scope = scope;

            const response = await getRaw("/ai-chat/stream", "POST", body, controller.signal);
            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n\n");
                buffer = lines.pop()!;

                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    const event: AiChatStreamEvent = JSON.parse(line.slice(6));

                    switch (event.type) {
                        case "sources":
                            sources = event.sources;
                            updateCurrentAssistantMessage();
                            break;
                        case "entity_references":
                            entityReferences = event.entityReferences;
                            updateCurrentAssistantMessage();
                            break;
                        case "results_truncated":
                            resultsTruncated = true;
                            updateCurrentAssistantMessage();
                            break;
                        case "deep_analysis":
                            usedDeepAnalysis = true;
                            updateCurrentAssistantMessage();
                            break;
                        case "tool_call":
                            appendActivity("tool_call", event.content ?? "", event.toolName, event.attachmentId);
                            break;
                        case "thinking":
                            appendActivity("thinking", event.content ?? "");
                            break;
                        case "delta":
                            streamedContent += event.content ?? "";
                            updateCurrentAssistantMessage();
                            break;
                        case "done":
                            messageId = event.messageId;
                            if (event.conversationId) {
                                if (conversationId === null) {
                                    bindTransientScopeToConversation(event.conversationId);
                                }
                                setConversationId(event.conversationId);
                            }
                            updateCurrentAssistantMessage();
                            queryClient.refetchQueries(aiChatConversationsQueryKey);
                            queryClient.refetchQueries(aiChatAttachmentsQueryKey);
                            break;
                        case "error":
                            setMessages((prev) => {
                                const updated = [...prev];
                                updated[updated.length - 1] = {
                                    role: "assistant",
                                    content: event.content ?? "An error occurred. Please try again.",
                                    sources: undefined,
                                };
                                return updated;
                            });
                            break;
                    }
                }
            }
        } catch (err) {
            const wasAborted = (err as { name?: string })?.name === "AbortError";
            if (!wasAborted) {
                setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0 && updated[updated.length - 1].role === "assistant" && !updated[updated.length - 1].content) {
                        updated[updated.length - 1] = {
                            role: "assistant",
                            content: "An error occurred. Please try again.",
                        };
                    }
                    return updated;
                });
            }
        } finally {
            if (abortControllerRef.current === controller) abortControllerRef.current = null;
            setIsStreaming(false);
        }
    }

    function stopStreaming() {
        abortControllerRef.current?.abort();
    }

    return {
        messages,
        isStreaming,
        conversationId,
        sendMessage,
        stopStreaming,
        startNewChat,
        loadConversation,
        ensureConversationId,
    };
}
